import React from 'react';
import {motion} from 'framer-motion';
import {useInView} from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiShield,FiEye,FiLock,FiMail,FiCalendar,FiDatabase,FiUser,FiGlobe}=FiIcons;

const Privacy=()=> {
const [headerRef,headerInView]=useInView({triggerOnce: true,threshold: 0.1});
const [contentRef,contentInView]=useInView({triggerOnce: true,threshold: 0.1});

const sections=[
{
icon: FiEye,
title: 'Information We Collect',
content: [
{
subtitle: 'Personal Information',
text: 'When you use our services,we may collect personal information such as your name,email address,phone number,company name,and job title. This information is collected when you register for our services,contact us,or engage with our platform.'
},
{
subtitle: 'Usage Information',
text: 'We automatically collect information about how you interact with our services,including your IP address,browser type,device information,pages visited,time spent on pages,and referral sources.'
},
{
subtitle: 'Cookies and Tracking Technologies',
text: 'We use cookies,web beacons,and similar tracking technologies to enhance your experience,analyze usage patterns,and provide personalized content. You can control cookie preferences through your browser settings or our cookie consent banner.'
}
]
},
{
icon: FiDatabase,
title: 'How We Use Your Information',
content: [
{
subtitle: 'Service Provision',
text: 'We use your information to provide,maintain,and improve our AI solutions and services,process transactions,and communicate with you about your account and our services.'
},
{
subtitle: 'Analytics and Improvement',
text: 'We analyze usage patterns to understand how our services are used,identify areas for improvement,and develop new features and functionalities.'
},
{
subtitle: 'Marketing Communications',
text: 'With your consent,we may send you marketing communications about our services,industry insights,and promotional offers. You can opt out of these communications at any time.'
},
{
subtitle: 'Legal Compliance',
text: 'We may use your information to comply with legal obligations,enforce our terms of service,and protect our rights and the rights of our users.'
}
]
},
{
icon: FiGlobe,
title: 'Information Sharing and Disclosure',
content: [
{
subtitle: 'Service Providers',
text: 'We may share your information with trusted third-party service providers who assist us in operating our business,such as cloud hosting providers,analytics services,and customer support platforms. These providers are contractually obligated to protect your information.'
},
{
subtitle: 'Business Transfers',
text: 'In the event of a merger,acquisition,or sale of assets,your information may be transferred as part of the business transaction. We will notify you of any such change in ownership or control of your personal information.'
},
{
subtitle: 'Legal Requirements',
text: 'We may disclose your information if required by law,court order,or government regulation,or if we believe disclosure is necessary to protect our rights,your safety,or the safety of others.'
},
{
subtitle: 'Consent',
text: 'We may share your information with your explicit consent or at your direction.'
}
]
},
{
icon: FiLock,
title: 'Data Security',
content: [
{
subtitle: 'Security Measures',
text: 'We implement industry-standard security measures to protect your information against unauthorized access,alteration,disclosure,or destruction. This includes encryption,secure data transmission,access controls,and regular security assessments.'
},
{
subtitle: 'Data Retention',
text: 'We retain your personal information for as long as necessary to provide our services,comply with legal obligations,resolve disputes,and enforce our agreements. When information is no longer needed,we securely delete or anonymize it.'
},
{
subtitle: 'Incident Response',
text: 'In the event of a data breach that affects your personal information,we will notify you and relevant authorities as required by applicable law,and take immediate steps to mitigate the impact.'
}
]
},
{
icon: FiUser,
title: 'Your Rights and Choices',
content: [
{
subtitle: 'Access and Correction',
text: 'You have the right to access,update,or correct your personal information. You can do this through your account settings or by contacting us directly.'
},
{
subtitle: 'Data Portability',
text: 'You have the right to request a copy of your personal information in a structured,machine-readable format,and to transfer this information to another service provider.'
},
{
subtitle: 'Deletion',
text: 'You have the right to request deletion of your personal information,subject to certain legal and contractual obligations. We will delete your information within a reasonable timeframe after verification of your request.'
},
{
subtitle: 'Marketing Opt-Out',
text: 'You can opt out of receiving marketing communications from us at any time by clicking the unsubscribe link in our emails or contacting us directly.'
},
{
subtitle: 'Cookie Management',
text: 'You can manage your cookie preferences through our cookie consent banner or your browser settings. Note that disabling certain cookies may affect the functionality of our services.'
}
]
}
];

return (
<div className="relative">
{/* Animated Background */}
<div className="absolute inset-0 overflow-hidden pointer-events-none">
<div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl animate-float"></div>
<div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-purple/10 rounded-full blur-3xl animate-float" style={{animationDelay: '2s'}}></div>
</div>

{/* Header */}
<section ref={headerRef} className="py-24 lg:py-32 relative">
<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
<motion.div
initial={{opacity: 0,y: 20}}
animate={headerInView ? {opacity: 1,y: 0} : {}}
transition={{duration: 0.6}}
className="text-center"
>
<h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-8">
<span className="bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent">
Privacy Policy
</span>
</h1>
<p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed mb-8">
Your privacy is important to us. This Privacy Policy explains how Reliabotix AI Solutions collects,uses,and protects your personal information when you use our services.
</p>
<div className="inline-flex items-center glass-card px-6 py-3 rounded-xl">
<SafeIcon icon={FiCalendar} className="w-5 h-5 text-primary-400 mr-3" />
<span className="text-gray-300">Last updated: January 15,2025</span>
</div>
</motion.div>
</div>
</section>

{/* Privacy Policy Content */}
<section ref={contentRef} className="py-20 relative">
<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
<div className="space-y-16">
{sections.map((section,index)=> (
<motion.div
key={index}
initial={{opacity: 0,y: 20}}
animate={contentInView ? {opacity: 1,y: 0} : {}}
transition={{duration: 0.6,delay: index * 0.1}}
className="bg-dark-800/30 backdrop-blur-sm rounded-3xl p-8 lg:p-12 border border-dark-700/30"
>
<div className="flex items-center mb-8">
<div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-primary-500 to-accent-purple rounded-2xl mr-6 shadow-lg">
<SafeIcon icon={section.icon} className="w-8 h-8 text-white" />
</div>
<h2 className="text-3xl lg:text-4xl font-bold text-white">{section.title}</h2>
</div>

<div className="space-y-8">
{section.content.map((item,itemIndex)=> (
<div key={itemIndex}>
<h3 className="text-xl font-semibold text-primary-300 mb-4">{item.subtitle}</h3>
<p className="text-gray-300 leading-relaxed">{item.text}</p>
</div>
))}
</div>
</motion.div>
))}
</div>
</div>
</section>

{/* International Data Transfers */}
<section className="py-16 relative">
<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
<div className="bg-dark-800/30 backdrop-blur-sm rounded-3xl p-8 border border-dark-700/30">
<h2 className="text-2xl font-bold text-white mb-6 flex items-center">
<SafeIcon icon={FiGlobe} className="w-6 h-6 text-primary-400 mr-3" />
International Data Transfers
</h2>
<p className="text-gray-300 leading-relaxed mb-4">
Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws. When we transfer your information internationally,we ensure appropriate safeguards are in place to protect your privacy and rights.
</p>
<p className="text-gray-300 leading-relaxed">
For transfers from the European Economic Area (EEA) to countries outside the EEA,we rely on adequacy decisions by the European Commission or implement appropriate safeguards such as Standard Contractual Clauses.
</p>
</div>
</div>
</section>

{/* Children's Privacy */}
<section className="py-16 relative">
<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
<div className="bg-dark-800/30 backdrop-blur-sm rounded-3xl p-8 border border-dark-700/30">
<h2 className="text-2xl font-bold text-white mb-6 flex items-center">
<SafeIcon icon={FiShield} className="w-6 h-6 text-primary-400 mr-3" />
Children's Privacy
</h2>
<p className="text-gray-300 leading-relaxed">
Our services are not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If we become aware that we have collected personal information from a child under 13,we will take steps to delete such information promptly. If you believe we have collected information from a child under 13,please contact us immediately.
</p>
</div>
</div>
</section>

{/* Changes to Privacy Policy */}
<section className="py-16 relative">
<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
<div className="bg-dark-800/30 backdrop-blur-sm rounded-3xl p-8 border border-dark-700/30">
<h2 className="text-2xl font-bold text-white mb-6">Changes to This Privacy Policy</h2>
<p className="text-gray-300 leading-relaxed mb-4">
We may update this Privacy Policy from time to time to reflect changes in our practices,technology,legal requirements,or other factors. When we make material changes,we will notify you by email (if you have provided an email address) or by posting a notice on our website prior to the changes taking effect.
</p>
<p className="text-gray-300 leading-relaxed">
We encourage you to review this Privacy Policy periodically to stay informed about how we collect,use,and protect your information. Your continued use of our services after any changes indicates your acceptance of the updated Privacy Policy.
</p>
</div>
</div>
</section>

{/* Contact Information */}
<section className="py-20 relative">
<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
<div className="bg-gradient-to-r from-primary-500/10 to-accent-purple/10 backdrop-blur-sm rounded-3xl p-8 border border-primary-500/20 text-center">
<h2 className="text-2xl font-bold text-primary-300 mb-6 flex items-center justify-center">
<SafeIcon icon={FiMail} className="w-6 h-6 mr-3" />
Contact Us About Privacy
</h2>
<p className="text-primary-200 mb-6 max-w-2xl mx-auto">
If you have any questions,concerns,or requests regarding this Privacy Policy or our data practices,please don't hesitate to contact us.
</p>
<div className="space-y-2 text-primary-200">
<p><strong>Email:</strong> info@reliabotix.com</p>
<p><strong>Address:</strong> Reliabotix AI Solutions,Data Protection Officer</p>
<p><strong>Phone:</strong> +1 (555) 123-4567</p>
</div>
<motion.div
whileHover={{scale: 1.05}}
whileTap={{scale: 0.95}}
className="mt-6"
>
<a
href="/#/contact"
className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-primary-500 to-accent-purple text-white rounded-2xl font-semibold hover:shadow-2xl hover:shadow-primary-500/25 transition-all duration-300"
>
Contact Our Privacy Team
</a>
</motion.div>
</div>
</div>
</section>
</div>
);
};

export default Privacy;